/**
 * Connect 4 Game Logic
 * Handles game state, move validation, and win detection
 */

const ROWS = 6;
const COLS = 7;

/**
 * Create a new empty board
 * @returns {Array} 6x7 array (rows x columns) of empty string values
 */
export const createEmptyBoard = () => {
  return Array(ROWS * COLS).fill('');
};

/**
 * Get the player color
 * @param {string} playerId - The player ID
 * @param {string} hostId - The host player ID
 * @returns {string} 'red' or 'yellow'
 */
export const getPlayerColor = (playerId, hostId) => {
  return playerId === hostId ? 'red' : 'yellow';
};

/**
 * Convert 2D coordinate to 1D index
 * @param {number} row - Row index (0-5)
 * @param {number} col - Column index (0-6)
 * @returns {number} 1D index
 */
export const getIndex = (row, col) => {
  return row * COLS + col;
};

/**
 * Convert 1D index to 2D coordinates
 * @param {number} index - 1D index
 * @returns {Object} {row, col}
 */
export const getCoordinates = (index) => {
  return {
    row: Math.floor(index / COLS),
    col: index % COLS,
  };
};

/**
 * Get the lowest available row in a column
 * @param {Array} board - Current board state
 * @param {number} col - Column index (0-6)
 * @returns {number|null} Row index or null if column is full
 */
export const getLowestAvailableRow = (board, col) => {
  for (let row = ROWS - 1; row >= 0; row--) {
    const index = getIndex(row, col);
    if (board[index] === '' || board[index] === null) {
      return row;
    }
  }
  return null;
};

/**
 * Check if a move is valid
 * @param {Array} board - Current board state
 * @param {number} col - Column index (0-6)
 * @returns {boolean} True if move is valid
 */
export const isValidMove = (board, col) => {
  if (col < 0 || col >= COLS) return false;
  return getLowestAvailableRow(board, col) !== null;
};

/**
 * Make a move on the board
 * @param {Array} board - Current board state
 * @param {number} col - Column to drop disc (0-6)
 * @param {string} color - Player color ('red' or 'yellow')
 * @returns {Object} {board: newBoard, row: rowPlaced}
 */
export const makeMove = (board, col, color) => {
  if (!isValidMove(board, col)) {
    return { board, row: null };
  }

  const row = getLowestAvailableRow(board, col);
  const index = getIndex(row, col);
  const newBoard = [...board];
  newBoard[index] = color;

  return { board: newBoard, row };
};

/**
 * Check if there's a winner
 * @param {Array} board - Current board state
 * @returns {Object|null} Winner info or null
 */
export const checkWinner = (board) => {
  // Check horizontal
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const indices = [
        getIndex(row, col),
        getIndex(row, col + 1),
        getIndex(row, col + 2),
        getIndex(row, col + 3),
      ];

      if (
        board[indices[0]] &&
        board[indices[0]] === board[indices[1]] &&
        board[indices[0]] === board[indices[2]] &&
        board[indices[0]] === board[indices[3]]
      ) {
        return {
          winner: board[indices[0]],
          combination: indices,
          direction: 'horizontal',
        };
      }
    }
  }

  // Check vertical
  for (let row = 0; row < ROWS - 3; row++) {
    for (let col = 0; col < COLS; col++) {
      const indices = [
        getIndex(row, col),
        getIndex(row + 1, col),
        getIndex(row + 2, col),
        getIndex(row + 3, col),
      ];

      if (
        board[indices[0]] &&
        board[indices[0]] === board[indices[1]] &&
        board[indices[0]] === board[indices[2]] &&
        board[indices[0]] === board[indices[3]]
      ) {
        return {
          winner: board[indices[0]],
          combination: indices,
          direction: 'vertical',
        };
      }
    }
  }

  // Check diagonal (top-left to bottom-right)
  for (let row = 0; row < ROWS - 3; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const indices = [
        getIndex(row, col),
        getIndex(row + 1, col + 1),
        getIndex(row + 2, col + 2),
        getIndex(row + 3, col + 3),
      ];

      if (
        board[indices[0]] &&
        board[indices[0]] === board[indices[1]] &&
        board[indices[0]] === board[indices[2]] &&
        board[indices[0]] === board[indices[3]]
      ) {
        return {
          winner: board[indices[0]],
          combination: indices,
          direction: 'diagonal-right',
        };
      }
    }
  }

  // Check diagonal (top-right to bottom-left)
  for (let row = 0; row < ROWS - 3; row++) {
    for (let col = 3; col < COLS; col++) {
      const indices = [
        getIndex(row, col),
        getIndex(row + 1, col - 1),
        getIndex(row + 2, col - 2),
        getIndex(row + 3, col - 3),
      ];

      if (
        board[indices[0]] &&
        board[indices[0]] === board[indices[1]] &&
        board[indices[0]] === board[indices[2]] &&
        board[indices[0]] === board[indices[3]]
      ) {
        return {
          winner: board[indices[0]],
          combination: indices,
          direction: 'diagonal-left',
        };
      }
    }
  }

  return null;
};

/**
 * Check if the board is full (draw)
 * @param {Array} board - Current board state
 * @returns {boolean} True if board is full
 */
export const isBoardFull = (board) => {
  return board.every((cell) => cell !== null && cell !== '');
};

/**
 * Check if the game is over
 * @param {Array} board - Current board state
 * @returns {Object} Game status
 */
export const getGameStatus = (board) => {
  const winner = checkWinner(board);

  if (winner) {
    return {
      isOver: true,
      winner: winner.winner,
      winningCombination: winner.combination,
      direction: winner.direction,
      isDraw: false,
    };
  }

  if (isBoardFull(board)) {
    return {
      isOver: true,
      winner: null,
      winningCombination: null,
      direction: null,
      isDraw: true,
    };
  }

  return {
    isOver: false,
    winner: null,
    winningCombination: null,
    direction: null,
    isDraw: false,
  };
};

/**
 * Get the next player's turn
 * @param {string} currentTurn - Current player's color
 * @returns {string} Next player's color
 */
export const getNextTurn = (currentTurn) => {
  return currentTurn === 'red' ? 'yellow' : 'red';
};

/**
 * Get the total number of moves made
 * @param {Array} board - Current board state
 * @returns {number} Number of moves
 */
export const getMoveCount = (board) => {
  return board.filter((cell) => cell !== null && cell !== '').length;
};

/**
 * Create initial game state
 * @param {string} hostId - Host player ID
 * @param {string} guestId - Guest player ID
 * @returns {Object} Initial game state
 */
export const createInitialGameState = (hostId, guestId) => {
  // Randomly decide who gets red (and goes first)
  const hostGoesFirst = Math.random() < 0.5;

  return {
    board: createEmptyBoard(),
    currentTurn: 'red', // Red always goes first
    hostColor: hostGoesFirst ? 'red' : 'yellow',
    guestColor: hostGoesFirst ? 'yellow' : 'red',
    winner: null,
    isDraw: false,
    isGameOver: false,
    winningCombination: null,
    direction: null,
    moveCount: 0,
    lastMove: null, // {row, col} of last move for animation
    startedAt: Date.now(),
  };
};

/**
 * Validate game state
 * @param {Object} gameState - Game state to validate
 * @returns {boolean} True if valid
 */
export const isValidGameState = (gameState) => {
  if (!gameState || typeof gameState !== 'object') {
    return false;
  }

  if (!Array.isArray(gameState.board) || gameState.board.length !== ROWS * COLS) {
    return false;
  }

  if (!['red', 'yellow'].includes(gameState.currentTurn)) {
    return false;
  }

  return true;
};
